import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import { LogIn, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { LoginSkeleton } from '../components/SkeletonLoader';

/**
 * Login page — Firebase email/password auth.
 * Minimal centered form. Redirects to admin or teacher dashboard based on claims.
 */
export function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [setupRole, setSetupRole] = useState('teacher');
  const { user, loading, signIn } = useAuth();
  const navigate    = useNavigate();

  // If already logged in, redirect away
  useEffect(() => {
    if (!loading && user) {
      user.getIdTokenResult().then(tokenResult => {
        const role = tokenResult.claims.role || 'teacher';
        navigate(role === 'admin' ? '/admin/dashboard' : '/teacher/dashboard', { replace: true });
      });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!navigator.onLine) {
        throw new Error('Network Error: Cannot authenticate offline');
      }
      
      const userCredential = await signIn(email, password);
      const u = userCredential.user;
      
      // Check RBAC claims
      const tokenResult = await u.getIdTokenResult();
      const role = tokenResult.claims.role || 'teacher'; // fallback to teacher
      
      // Issue 3 Fix: Cache offline identity and PIN using proper UI
      if (!localStorage.getItem('offline_pin')) {
        setSetupRole(role);
        setShowPinModal(true);
      } else {
        localStorage.setItem('offline_role', role);
        if (role === 'admin') navigate('/admin/dashboard');
        else navigate('/teacher/dashboard');
      }
    } catch (err) {
      if (err.message.includes('Network Error')) {
        setError(err.message);
      } else {
        setError('Invalid Credentials');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError('');
    } catch (err) {
      setError('Could not send reset email. Check the address and try again.');
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput.length >= 4) {
      localStorage.setItem('offline_pin', pinInput);
      localStorage.setItem('offline_role', setupRole);
      setShowPinModal(false);
      navigate(setupRole === 'admin' ? '/admin/dashboard' : '/teacher/dashboard');
    }
  };

  // Don't render form while checking auth state
  if (loading) {
    return <LoginSkeleton />;
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* ── Logo / Title ── */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-indigo-50 mb-4 overflow-hidden">
            <img
              src="/logo-circle.png"
              alt="Shiksha-Edge Logo"
              className="w-16 h-16 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span class="text-indigo-600 font-bold text-2xl">SE</span>';
              }}
            />
          </div>
          <img
            src="/logo-wide.png"
            alt="RSBRTL Shiksha-Edge"
            className="mx-auto h-10 mb-2 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="text-2xl font-bold tracking-tight text-ink">
            Shiksha-Edge
          </h1>
          <p className="text-sm text-graphite mt-1 font-secondary">
            RSBRTL School Management System
          </p>
        </div>

        {/* ── Forgot Password View ── */}
        {showForgot ? (
          <div className="space-y-4">
            {resetSent ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                  <span className="text-green-600 text-xl">✓</span>
                </div>
                <p className="text-sm font-medium text-ink text-center">
                  Password reset email sent to <strong>{email}</strong>.
                </p>
                <button
                  onClick={() => { setShowForgot(false); setResetSent(false); }}
                  className="text-sm text-indigo-600 font-medium hover:underline"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setError(''); }}
                  className="flex items-center gap-1 text-sm text-graphite hover:text-ink"
                >
                  <ArrowLeft size={14} /> Back to Login
                </button>

                <p className="text-sm text-graphite">
                  Enter your email and we'll send a reset link.
                </p>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-conflict text-sm">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                  placeholder="teacher@school.edu"
                />

                <button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors duration-200 shadow-sm"
                >
                  Send Reset Link
                </button>
              </form>
            )}
          </div>
        ) : (
          /* ── Login Form ── */
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-conflict text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-ink mb-1 font-secondary">
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                placeholder="teacher@school.edu"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="login-password" className="block text-sm font-medium text-ink font-secondary">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setError(''); }}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Forgot Password?
                </button>
              </div>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <LogIn size={18} />
              )}
              {isLoading ? 'Signing in…' : 'Login securely'}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-graphite mt-8 font-secondary">
          Offline-first · Zero cloud costs · Built for Bhuratali
        </p>
      </div>

      {/* Offline PIN Setup Modal */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full">
            <h2 className="text-xl font-bold text-ink mb-2">Set Offline PIN</h2>
            <p className="text-sm text-graphite mb-4">
              To allow you to log in and enter data even when there is no internet connection, please set a 4-digit PIN for this device.
            </p>
            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="4-Digit PIN"
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none mb-4"
                required
              />
              <button
                type="submit"
                disabled={pinInput.length < 4}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg px-4 py-3 font-semibold transition-colors"
              >
                Save PIN & Continue
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
